import React from "react";
import { ActionIcon, Box, Drawer, Text } from "@mantine/core";
import { IconX } from "@tabler/icons-react";
import { MOBILE_Z } from "../tokens";
import styles from "./MobileSheet.module.css";

export interface MobileSheetProps {
  opened: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  /** Replaces the title / subtitle block (e.g. the detail sheet's status
   *  chip + client link). `title` still labels the dialog. */
  header?: React.ReactNode;
  /** Mantine's window-level Escape close. Off when the sheet routes Escape
   *  itself (onKeyDown), so a picker / panel closes before the sheet. */
  closeOnEscape?: boolean;
  onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>;
  "data-testid"?: string;
}

/**
 * Sheets mount immediately (no Mantine transition); the 240ms slide-up and
 * the 200ms scrim fade are CSS animations on open (`sheetMotion` classes),
 * so nothing waits on a JS transition.
 */
export const SHEET_TRANSITION = { duration: 0 } as const;

/** Classes that animate a sheet's content / scrim in. */
export const sheetMotion = {
  content: styles.slideUp,
  overlay: styles.scrimIn,
};

/**
 * Bottom sheet over a `rgba(20,20,20,.34)` scrim: drag handle, titled header
 * with a close X, scrolling body; slides up in 240ms. Scrim, X and Escape all
 * close it. Sits below Mantine's modal layer (MOBILE_Z.sheet) so modals
 * opened from it stack above.
 */
export const MobileSheet: React.FC<MobileSheetProps> = ({
  opened,
  onClose,
  title,
  subtitle,
  children,
  header,
  closeOnEscape = true,
  onKeyDown,
  "data-testid": testId,
}) => (
  <Drawer
    opened={opened}
    onClose={onClose}
    position="bottom"
    withCloseButton={false}
    zIndex={MOBILE_Z.sheet}
    closeOnEscape={closeOnEscape}
    padding={0}
    transitionProps={SHEET_TRANSITION}
    overlayProps={{
      color: "#141414",
      backgroundOpacity: 0.34,
      blur: 0,
      className: sheetMotion.overlay,
    }}
    classNames={{
      content: `${styles.content} ${sheetMotion.content}`,
      body: styles.body,
    }}
    styles={{ content: { height: "auto", maxHeight: "82dvh" } }}
    aria-label={title}
  >
    <Box data-testid={testId} className={styles.body} onKeyDown={onKeyDown}>
      <Box className={styles.handle} aria-hidden="true" />
      <Box className={styles.head}>
        {header ?? (
          <Box className={styles.titles}>
            <Text className={styles.title}>{title}</Text>
            {subtitle && <Text className={styles.subtitle}>{subtitle}</Text>}
          </Box>
        )}
        <ActionIcon
          variant="default"
          className={styles.close}
          onClick={onClose}
          aria-label="Close"
        >
          <IconX size={17} stroke={1.8} />
        </ActionIcon>
      </Box>
      <Box className={styles.scroll}>{children}</Box>
    </Box>
  </Drawer>
);
